import {
  Badge, Button, Card, DateInput, ErrorState, Field, LARGEST_PAGE, LoadingState, Notice, Permissions, Row, Select,
  Stack, Textarea, invalidDateIn, useApi, useI18n, usePermission, type ChildExportResponse, type GuardianLinkResponse,
  type PagedResponse,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

/**
 * The encrypted copies of a child's records the administration makes for a guardian, and the photos handed over by
 * hand (Q41, Q51).
 *
 * Guardians download nothing. Here the Owner or Manager makes a copy for one guardian; its password goes by SMS to
 * that guardian alone and is shown nowhere, so the file this card saves can be handed over but not opened here. The
 * reason a guardian cannot be sent one comes from the API, in the words the request itself would use.
 */
export function ExportsCard({ childId, justWithdrawn }: { childId: number; justWithdrawn: boolean }) {
  const { t } = useI18n();
  const canExport = usePermission(Permissions.children.export);
  const canUpdate = usePermission(Permissions.children.update);

  if (!canExport && !canUpdate) return null;

  return (
    <Card title={t('exports.title')} hint={t('exports.hint')}>
      <Stack>
        {justWithdrawn && canExport && <Notice tone="info">{t('exports.afterWithdrawal')}</Notice>}
        {canExport && <Copies childId={childId} />}
        {canUpdate && <PhotoHandovers childId={childId} />}
      </Stack>
    </Card>
  );
}

function Copies({ childId }: { childId: number }) {
  const api = useApi();
  const { t, formatDate } = useI18n();
  const queryClient = useQueryClient();

  // Under the child's key, so whatever refreshes the child (a withdrawal, a re-enrolment) refreshes these too.
  const recipients = useQuery({
    queryKey: ['child', childId, 'export-recipients'],
    queryFn: () => api.exports.recipients(childId, { pageSize: LARGEST_PAGE }),
  });
  const copies = useQuery({
    queryKey: ['child', childId, 'exports'],
    queryFn: () => api.exports.list(childId, { pageSize: LARGEST_PAGE }),
  });

  const make = useMutation({
    mutationFn: (guardianLinkId: number) => api.exports.create(childId, guardianLinkId),
    onSuccess: (created) => {
      // The list is newest first, so the new copy heads it; the API answered with the row the list would show. A
      // refetch that landed while the copy was being made may already hold it.
      queryClient.setQueryData<PagedResponse<ChildExportResponse>>(['child', childId, 'exports'], (current) => {
        if (!current || current.items.some((copy) => copy.id === created.id)) return current;
        const totalCount = current.totalCount + 1;
        return {
          ...current,
          items: [created, ...current.items],
          totalCount,
          totalPages: Math.ceil(totalCount / current.pageSize),
        };
      });
      // The guardian just sent one may not be sent another until Exports:RepeatInterval has passed.
      void queryClient.invalidateQueries({ queryKey: ['child', childId, 'export-recipients'] });
    },
  });

  const download = useMutation({
    mutationFn: async (exportId: number) => {
      saveFile(await api.exports.file(childId, exportId), `copy-${childId}-${exportId}.pdf`);
    },
    // Each download is counted on the copy.
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['child', childId, 'exports'] }),
  });

  return (
    <Stack>
      <strong>{t('exports.recipients')}</strong>
      {recipients.isPending && <LoadingState />}
      {recipients.isError && <ErrorState error={recipients.error} onRetry={() => void recipients.refetch()} />}
      {make.isError && <ErrorState error={make.error} />}
      {make.isSuccess && <Notice tone="success">{t('exports.made', { name: make.data.guardianName ?? '—' })}</Notice>}

      {recipients.data?.items.map((recipient) => (
        <Row key={recipient.guardianLinkId} between>
          <span className="dg-stack dg-stack--tight">
            <strong>{recipient.guardianName ?? '—'}</strong>
            <span className="dg-row">
              <span className="dg-card__hint">{t(`guardians.relationship.${recipient.relationship}`)}</span>
              {recipient.isPrimary && <Badge tone="neutral">{t('guardians.primary')}</Badge>}
              {recipient.leftOn && (
                <span className="dg-card__hint">{t('exports.ended', { date: formatDate(recipient.leftOn) })}</span>
              )}
            </span>
            {recipient.refusalDetail && <span className="dg-card__hint">{recipient.refusalDetail}</span>}
          </span>
          <Button
            disabled={recipient.refusal !== null}
            busy={make.isPending && make.variables === recipient.guardianLinkId}
            busyLabel={t('action.saving')}
            onClick={() => make.mutate(recipient.guardianLinkId)}
          >
            {t('exports.make')}
          </Button>
        </Row>
      ))}

      <strong>{t('exports.list')}</strong>
      {copies.isPending && <LoadingState />}
      {copies.isError && <ErrorState error={copies.error} onRetry={() => void copies.refetch()} />}
      {download.isError && <ErrorState error={download.error} />}
      {copies.data?.items.length === 0 && <p className="dg-card__hint">{t('exports.none')}</p>}

      {copies.data?.items.map((copy) => (
        <Row key={copy.id} between>
          <span className="dg-stack dg-stack--tight">
            <strong>{copy.guardianName ?? '—'}</strong>
            <span className="dg-card__hint">
              {t('exports.covers', { from: formatDate(copy.coversFrom), to: formatDate(copy.coversTo) })}
            </span>
            <span className="dg-card__hint">
              {t('exports.madeBy', { name: copy.requestedByName ?? '—', date: formatDate(copy.createdAt) })}
            </span>
            <span className="dg-card__hint">{t('exports.downloads', { count: copy.downloadCount })}</span>
            {copy.hasFile && (
              <span className="dg-card__hint">{t('exports.keptUntil', { date: formatDate(copy.fileExpiresAt) })}</span>
            )}
          </span>
          {copy.hasFile ? (
            <Button
              variant="ghost"
              busy={download.isPending && download.variables === copy.id}
              busyLabel={t('exports.downloading')}
              onClick={() => download.mutate(copy.id)}
            >
              {t('exports.download')}
            </Button>
          ) : (
            <Badge tone="neutral">{t('exports.fileGone')}</Badge>
          )}
        </Row>
      ))}
    </Stack>
  );
}

function PhotoHandovers({ childId }: { childId: number }) {
  const api = useApi();
  const { t, formatDate } = useI18n();
  const queryClient = useQueryClient();
  const [guardianLinkId, setGuardianLinkId] = useState<number | ''>('');
  const [handedOverOn, setHandedOverOn] = useState('');
  const [note, setNote] = useState('');
  const [incomplete, setIncomplete] = useState(false);

  // Ended links too: the photos are often handed over as the child leaves, when every link has just ended. Who of
  // them may receive anything is handOverCandidates'.
  const guardians = useQuery({
    queryKey: ['child', childId, 'guardians', true],
    queryFn: () => api.guardians.forChild(childId, true),
  });
  const handovers = useQuery({
    queryKey: ['child', childId, 'photo-handovers'],
    queryFn: () => api.photoHandovers.list(childId, { pageSize: LARGEST_PAGE }),
  });

  const record = useMutation({
    mutationFn: (linkId: number) => api.photoHandovers.record(childId, {
      guardianLinkId: linkId,
      handedOverOn: handedOverOn || null,
      note: note.trim() || null,
    }),
    onSuccess: () => {
      setGuardianLinkId('');
      setHandedOverOn('');
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['child', childId, 'photo-handovers'] });
    },
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const unreadable = invalidDateIn(event.currentTarget);
    if (unreadable) { unreadable.focus(); return; }
    setIncomplete(guardianLinkId === '');
    if (guardianLinkId !== '') record.mutate(guardianLinkId);
  }

  return (
    <Stack>
      <strong>{t('handover.title')}</strong>
      <span className="dg-card__hint">{t('handover.hint')}</span>
      {handovers.isPending && <LoadingState />}
      {handovers.isError && <ErrorState error={handovers.error} onRetry={() => void handovers.refetch()} />}
      {handovers.data?.items.length === 0 && <p className="dg-card__hint">{t('handover.none')}</p>}
      {handovers.data?.items.map((handover) => (
        <span key={handover.id} className="dg-stack dg-stack--tight">
          <span>
            {t('handover.entry', {
              date: formatDate(handover.handedOverOn),
              name: handover.guardianName ?? '—',
              by: handover.recordedByName ?? '—',
            })}
          </span>
          {handover.note && <span className="dg-card__hint">{handover.note}</span>}
        </span>
      ))}

      <form onSubmit={submit} noValidate>
        <Stack>
          {record.isError && <ErrorState error={record.error} />}
          {incomplete && <p className="dg-field__error" role="alert">{t('handover.guardianRequired')}</p>}
          <Row>
            <Field label={t('handover.guardian')}>
              {(props) => (
                <Select
                  {...props}
                  value={guardianLinkId}
                  onChange={(event) => setGuardianLinkId(event.target.value === '' ? '' : Number(event.target.value))}
                >
                  <option value="">—</option>
                  {handOverCandidates(guardians.data ?? []).map((guardian) => (
                    <option key={guardian.id} value={guardian.id}>{guardian.fullName ?? guardian.phone}</option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('handover.date')}>
              {(props) => <DateInput {...props} value={handedOverOn} onChange={setHandedOverOn} />}
            </Field>
          </Row>
          <Field label={t('handover.note')}>
            {(props) => <Textarea {...props} value={note} onChange={(event) => setNote(event.target.value)} />}
          </Field>
          <Row>
            <Button type="submit" busy={record.isPending} busyLabel={t('action.saving')}>{t('handover.record')}</Button>
          </Row>
        </Stack>
      </form>
    </Stack>
  );
}

/**
 * The guardians photos may be handed to, by each one's latest link to the child: not suspended, not unlinked, and a
 * membership not deactivated. A family that left keeps its place. The API asks again (ExportRules.HandOverRefusal);
 * this only keeps the list to the guardians it would accept.
 */
function handOverCandidates(links: GuardianLinkResponse[]): GuardianLinkResponse[] {
  const latest = new Map<number, number>();
  for (const link of links) {
    latest.set(link.guardianMembershipId, Math.max(link.id, latest.get(link.guardianMembershipId) ?? link.id));
  }
  return links.filter((link) =>
    latest.get(link.guardianMembershipId) === link.id
    && link.accessLevel !== 'Suspended'
    && link.endedReason !== 'Unlinked'
    && link.status !== 'Deactivated');
}

/**
 * Saves a file the browser holds. The one file the console saves rather than shows (Q7): an encrypted copy, which
 * opens only with the password its guardian was sent.
 */
function saveFile(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  // After the click has handed the file to the browser's download, not before.
  window.setTimeout(() => URL.revokeObjectURL(url));
}
